import { css } from 'styled-components';
import { theme } from './GlobalStyles';

// Media query helpers using theme breakpoints
export const mobile = (styles: any) => css`
  @media (max-width: ${theme.breakpoints.mobile}) {
    ${styles}
  }
`;

export const tablet = (styles: any) => css`
  @media (min-width: ${theme.breakpoints.mobile}) and (max-width: ${theme.breakpoints.tablet}) {
    ${styles}
  }
`;

export const desktop = (styles: any) => css`
  @media (min-width: ${theme.breakpoints.desktop}) {
    ${styles}
  }
`;

// Touch-friendly button styles
export const touchButton = css`
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  
  ${mobile(css`
    &:hover {
      /* Disable hover effects on touch devices */
      transform: none !important;
    }
    
    &:active {
      transform: scale(0.98);
      opacity: 0.8;
    }
  `)}
`;

// Safe area padding for devices with notches
export const safeAreaPadding = css`
  padding-top: max(${theme.spacing.md}, env(safe-area-inset-top));
  padding-bottom: max(${theme.spacing.md}, env(safe-area-inset-bottom));
  padding-left: max(${theme.spacing.md}, env(safe-area-inset-left));
  padding-right: max(${theme.spacing.md}, env(safe-area-inset-right));
`;

// Smooth scrolling for mobile
export const smoothScroll = css`
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
`;

// Prevent text selection on UI elements
export const noSelect = css`
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

// Mobile-optimized input styles
export const mobileInput = css`
  font-size: 16px; /* Prevent zoom on iOS */
  min-height: ${theme.mobile.minTouchTarget};
  
  ${mobile(css`
    border-radius: 12px;
    padding: ${theme.spacing.md};
    
    &:focus {
      transform: none;
    }
  `)}
`;

// Mobile-friendly modal styles
export const mobileModal = css`
  ${mobile(css`
    margin: ${theme.spacing.md};
    max-height: calc(100vh - ${theme.spacing.xl});
    border-radius: 16px;
    
    .modal-content {
      max-height: calc(100vh - ${theme.spacing.xxl});
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  `)}
`;

// Responsive grid system
export const responsiveGrid = (columns: { mobile?: number; tablet?: number; desktop?: number }) => css`
  display: grid;
  gap: ${theme.spacing.md};
  grid-template-columns: repeat(${columns.mobile || 1}, 1fr);
  
  ${tablet(css`
    grid-template-columns: repeat(${columns.tablet || columns.mobile || 2}, 1fr);
  `)}
  
  ${desktop(css`
    grid-template-columns: repeat(${columns.desktop || columns.tablet || columns.mobile || 3}, 1fr);
  `)}
`;

// Mobile-optimized card styles
export const mobileCard = css`
  border-radius: 16px;
  padding: ${theme.spacing.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  
  ${mobile(css`
    border-radius: 12px;
    padding: ${theme.spacing.md};
    margin: 0 ${theme.spacing.sm};
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  `)}
`;

// Sticky header for mobile
export const stickyMobileHeader = css`
  ${mobile(css`
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
  `)}
`;

// Mobile-friendly spacing
export const mobileSpacing = {
  xs: css`
    ${mobile(css`
      margin: ${theme.spacing.xs};
      padding: ${theme.spacing.xs};
    `)}
  `,
  sm: css`
    ${mobile(css`
      margin: ${theme.spacing.sm};
      padding: ${theme.spacing.sm};
    `)}
  `,
  md: css`
    ${mobile(css`
      margin: ${theme.spacing.md};
      padding: ${theme.spacing.md};
    `)}
  `,
  lg: css`
    ${mobile(css`
      margin: ${theme.spacing.lg};
      padding: ${theme.spacing.lg};
    `)}
  `,
};

// Utility for hiding elements on mobile
export const hideOnMobile = css`
  ${mobile(css`
    display: none !important;
  `)}
`;

// Utility for showing elements only on mobile
export const showOnMobile = css`
  display: none;
  
  ${mobile(css`
    display: block;
  `)}
`;

// Mobile-optimized typography
export const mobileTypography = {
  heading: css`
    ${mobile(css`
      font-size: 1.5rem;
      line-height: 1.3;
      margin-bottom: ${theme.spacing.sm};
    `)}
  `,
  subheading: css`
    ${mobile(css`
      font-size: 1.25rem;
      line-height: 1.4;
      margin-bottom: ${theme.spacing.sm};
    `)}
  `,
  body: css`
    ${mobile(css`
      font-size: 1rem;
      line-height: 1.5;
    `)}
  `,
  small: css`
    ${mobile(css`
      font-size: 0.875rem;
      line-height: 1.4;
    `)}
  `,
};

export default {
  mobile,
  tablet,
  desktop,
  touchButton,
  safeAreaPadding,
  smoothScroll,
  noSelect,
  mobileInput,
  mobileModal,
  responsiveGrid,
  mobileCard,
  stickyMobileHeader,
  mobileSpacing,
  hideOnMobile,
  showOnMobile,
  mobileTypography,
}; 